import * as THREE from 'three';
import type { Anim } from '../anim';
import { Animated, Bob } from '../animated';
import { FUTURE } from '../materials';
import { Beam, type ModelProps } from '../parts';

/** Future-era units: each sits on y = 0, faces +z, at real proportions (UnitModel scales them). */

const { HULL, PANEL, DARK_PANEL, SHADOW } = FUTURE;

const G = {
  // Mech
  foot: new THREE.BoxGeometry(0.07, 0.03, 0.13),
  pelvis: new THREE.BoxGeometry(0.2, 0.05, 0.1),
  hipJoint: new THREE.SphereGeometry(0.035, 8, 6),
  torso: new THREE.BoxGeometry(0.24, 0.15, 0.17),
  chestPlate: new THREE.BoxGeometry(0.18, 0.08, 0.02),
  cockpit: new THREE.BoxGeometry(0.12, 0.07, 0.11),
  visor: new THREE.BoxGeometry(0.09, 0.022, 0.01),
  pack: new THREE.BoxGeometry(0.16, 0.12, 0.06),
  vent: new THREE.BoxGeometry(0.03, 0.06, 0.01),
  shoulder: new THREE.BoxGeometry(0.07, 0.07, 0.09),
  cannon: new THREE.CylinderGeometry(0.022, 0.026, 0.22, 8).rotateX(Math.PI / 2),
  muzzle: new THREE.CylinderGeometry(0.016, 0.016, 0.012, 8).rotateX(Math.PI / 2),
  // Drone swarm
  droneBody: new THREE.BoxGeometry(0.07, 0.025, 0.07),
  droneTop: new THREE.CylinderGeometry(0.03, 0.036, 0.018, 8),
  droneLight: new THREE.BoxGeometry(0.02, 0.012, 0.012),
  rotorHub: new THREE.CylinderGeometry(0.008, 0.008, 0.018, 6),
  blade: new THREE.BoxGeometry(0.07, 0.003, 0.012),
  shadow: new THREE.CircleGeometry(0.06, 12).rotateX(-Math.PI / 2),
};

const HIP_Y = 0.25;
const LEG_X = 0.08;
// Knees bent forward, ankles back under the hips (a digitigrade stance reads as a walker).
const KNEE: [number, number] = [0.13, 0.06];
const ANKLE_Z = -0.01;
const SWAY_SPEED = 0.9; // radians per second of the idle cycle
const SWAY_YAW = 0.07;
const SWAY_BOB = 0.006;

/**
 * A biped walker: pale armoured legs with forward knees, a team-coloured torso
 * with a hull chest plate, a small cockpit with a glowing visor, twin arm
 * cannons with glowing muzzles and a finned power pack on its back. The upper
 * body sways gently while it stands.
 */
export function Mech({ accent, glow }: ModelProps) {
  return (
    <>
      {[-LEG_X, LEG_X].map((x) => {
        const hip: [number, number, number] = [x, HIP_Y, 0];
        const knee: [number, number, number] = [x * 1.1, KNEE[0], KNEE[1]];
        const ankle: [number, number, number] = [x * 1.1, 0.03, ANKLE_Z];
        return (
          <group key={x}>
            <Beam from={hip} to={knee} material={PANEL} thickness={0.055} />
            <Beam from={knee} to={ankle} material={HULL} thickness={0.045} />
            <mesh geometry={G.hipJoint} material={DARK_PANEL} position={knee} />
            <mesh geometry={G.foot} material={DARK_PANEL} position={[x * 1.1, 0.015, 0.02]} />
          </group>
        );
      })}
      <mesh geometry={G.pelvis} material={DARK_PANEL} position={[0, HIP_Y, 0]} />
      {/* Bobs at twice the sway's rate (Bob sways at half its own). */}
      <Bob amount={SWAY_BOB} speed={SWAY_SPEED * 2} yaw={SWAY_YAW} position={[0, HIP_Y, 0]}>
        <mesh geometry={G.torso} material={accent} position={[0, 0.1, 0]} />
        <mesh geometry={G.chestPlate} material={HULL} position={[0, 0.09, 0.09]} />
        <mesh geometry={G.cockpit} material={HULL} position={[0, 0.2, 0.02]} />
        <mesh geometry={G.visor} material={glow} position={[0, 0.205, 0.078]} />
        <mesh geometry={G.pack} material={DARK_PANEL} position={[0, 0.12, -0.11]} />
        {[-0.04, 0, 0.04].map((x) => (
          <mesh key={x} geometry={G.vent} material={glow} position={[x, 0.13, -0.141]} />
        ))}
        {[-1, 1].map((side) => (
          <group key={side} position={[side * 0.155, 0.13, 0]}>
            <mesh geometry={G.shoulder} material={HULL} />
            <mesh geometry={G.cannon} material={DARK_PANEL} position={[side * 0.01, -0.06, 0.07]} />
            <mesh geometry={G.muzzle} material={glow} position={[side * 0.01, -0.06, 0.185]} />
          </group>
        ))}
      </Bob>
    </>
  );
}

// Drones in a loose triangle, lead drone in front: position, hover height and bob phase.
const DRONES = [
  { x: 0, z: 0.14, y: 0.34, phase: 0 },
  { x: -0.14, z: -0.08, y: 0.28, phase: 2.1 },
  { x: 0.14, z: -0.08, y: 0.31, phase: 4.2 },
];
const ROTOR_ARM = 0.06;
const ROTORS: [number, number][] = [
  [ROTOR_ARM, ROTOR_ARM],
  [-ROTOR_ARM, ROTOR_ARM],
  [ROTOR_ARM, -ROTOR_ARM],
  [-ROTOR_ARM, -ROTOR_ARM],
];
const BOB = 0.02;
const BOB_SPEED = 2.2; // radians per second
const ROTOR_SPEED = 40; // radians per second

const ROTOR_ANIMS: Anim[][] = DRONES.map((d) =>
  ROTORS.map((_, r) => ({
    bob: { amount: BOB, speed: BOB_SPEED, phase: d.phase },
    spin: { axis: 'y', speed: ROTOR_SPEED * (r % 2 === 0 ? 1 : -1) },
  }))
);

/**
 * Three quad-drones hovering in a loose triangle: pale bodies with a
 * team-coloured top, crossed arms with a spinning rotor at each end and
 * glowing lights at the front. Each bobs on its own beat over a soft shadow.
 */
export function DroneSwarm({ accent, glow }: ModelProps) {
  return (
    <>
      {DRONES.map((d, i) => {
        const bob = { amount: BOB, speed: BOB_SPEED, phase: d.phase };
        return (
          <group key={d.phase}>
            <mesh geometry={G.shadow} material={SHADOW} position={[d.x, 0.005, d.z]} />
            <Bob {...bob} position={[d.x, d.y, d.z]}>
              <mesh geometry={G.droneBody} material={HULL} />
              <mesh geometry={G.droneTop} material={accent} position={[0, 0.02, 0]} />
              <mesh geometry={G.droneLight} material={glow} position={[0, 0, 0.037]} />
              {ROTORS.map(([x, z]) => (
                <group key={`${x}:${z}`}>
                  <Beam
                    from={[0, 0, 0]}
                    to={[x, 0.01, z]}
                    material={DARK_PANEL}
                    thickness={0.012}
                  />
                  <mesh geometry={G.rotorHub} material={DARK_PANEL} position={[x, 0.012, z]} />
                </group>
              ))}
            </Bob>
            {/*
              Rotors bob with their drone and spin (animations don't nest, so
              each carries both). Neighbouring rotors spin opposite ways, as on
              a real quadcopter.
            */}
            {ROTORS.map(([x, z], r) => (
              <Animated
                key={`${x}:${z}`}
                anim={ROTOR_ANIMS[i][r]}
                position={[d.x + x, d.y + 0.022, d.z + z]}
              >
                <mesh geometry={G.blade} material={PANEL} />
                <mesh geometry={G.blade} material={PANEL} rotation={[0, Math.PI / 2, 0]} />
              </Animated>
            ))}
          </group>
        );
      })}
    </>
  );
}
